import { OperatorWithStatus } from "gql/generated";
import { useCallback, useMemo, useState } from "react";
import { getMultilingualString } from "shared/utils";

const NUMBERS_GROUP = "#";
const NUMBERS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

export interface IAlphabetic {
  letter: string;
  operators: OperatorWithStatus[];
}

export interface IAlphabetItem {
  letter: string;
  type: "letter" | "operator";
  operator?: OperatorWithStatus;
  virtualizerOffset: number;
}

export const useAlphabet = (
  numOperatorsToShowAlphabet: number = 25,
  letterItemHeight: number = 37,
  gapBetweenGroups: number = 12,
  operatorItemHeight: number = 84,
) => {
  const alphabet = useMemo(
    () => [..."abcdefghijklmnopqrstuvwxyz", NUMBERS_GROUP],
    [],
  );
  const [alphabetGroups, setAlphabetGroups] = useState<IAlphabetic[]>([]);
  // A new flattened alphabet groups array is created for use by the virtualizer
  const [flattenedAlphabetGroups, setFlattenedAlphabetGroups] = useState<
    IAlphabetItem[]
  >([]);
  const [virtualizedHeight, setVirtualizedHeight] = useState(0);

  const createAlphabetGroups = useCallback(
    (operators: OperatorWithStatus[]) => {
      const alphabetic: IAlphabetic[] = [];
      alphabet.forEach((letter, i) => {
        alphabetic.push({ letter, operators: [] });
        // Get all of the operators that start with that letter
        operators.forEach((operator) => {
          const firstLetter = operator.name.en?.toLowerCase().charAt(0) ?? "";
          if (
            firstLetter === letter ||
            (letter === NUMBERS_GROUP && NUMBERS.includes(firstLetter))
          ) {
            alphabetic[i].operators.push(operator);
          }
        });
        alphabetic[i].operators.sort((a, b) =>
          getMultilingualString(a.name) > getMultilingualString(b.name)
            ? 1
            : -1,
        );
      });
      setAlphabetGroups(alphabetic);

      const showAlphabet = operators.length > numOperatorsToShowAlphabet;

      // Calculate the virtualizer offset for each item in the flattened alphabet groups array
      let virtualizerOffset = 0;
      let groupsWithOperators = 0;
      setFlattenedAlphabetGroups(
        alphabetic.flatMap((group) => {
          if (group.operators.length === 0) {
            return [];
          }
          const groupItems: IAlphabetItem[] = [];

          // Increment the virtualizerOffset for the letter item
          groupsWithOperators++;
          if (showAlphabet) {
            if (groupsWithOperators > 1) {
              virtualizerOffset += gapBetweenGroups;
            }

            groupItems.push({
              type: "letter",
              letter: group.letter,
              virtualizerOffset,
            });
            virtualizerOffset += letterItemHeight;
          }

          // Add all of the operators to the groupItems
          group.operators.forEach((operator) => {
            // Add the operator item and increment the virtualizerOffset
            groupItems.push({
              letter: group.letter,
              type: "operator",
              operator,
              virtualizerOffset,
            } as IAlphabetItem);

            virtualizerOffset += operatorItemHeight;
          });
          return groupItems;
        }),
      );
      setVirtualizedHeight(virtualizerOffset);
    },
    [
      alphabet,
      gapBetweenGroups,
      letterItemHeight,
      numOperatorsToShowAlphabet,
      operatorItemHeight,
    ],
  );

  return {
    alphabet,
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    createAlphabetGroups,
  };
};
